import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { addonService } from 'api/services/addonService';
import { formatCurrency } from '../booking.utils';
import type { BookingAddonRequest } from 'api/types/booking.types';

/**
 * One row in the AddonsSection. Carries a stable client-side key (`_rowKey`) so
 * React can identify rows across re-renders without depending on the optional
 * server-side `id`. Pure UI concern — not sent to the API.
 */
export interface AddonRow extends BookingAddonRequest {
  /**
   * Agreed unit amount when the service was negotiated off its standard price
   * (D3 rate adjustments). `unitAmount` keeps the standard; the delta is
   * recorded separately so Transport / other-service revenue stays accurate.
   */
  agreedUnitAmount?: number;
  /** Negotiation reason — mandatory when the agreed amount is a discount. */
  adjustmentRemarks?: string;
  /** Stable client key for React. Generated on add; preserved on edit. */
  _rowKey: string;
  /** Display label for the selected master row (snapshot — survives master rename). */
  addonLabel?: string;
}

interface AddonsSectionProps {
  /** Property scope — used to filter the add-on master dropdown to the right property. */
  propertyId: string | undefined;
  rows: AddonRow[];
  onChange: (rows: AddonRow[]) => void;
  expanded: boolean;
  onToggle: () => void;
  /** Disables editing — used in view mode. */
  readOnly?: boolean;
  /** Stay window — bounds the service-date picker and seeds new rows. */
  checkInDate?: string;
  checkOutDate?: string;
}

/**
 * Generates a stable client-side row key. Not persisted.
 */
let _addonKeyCounter = 0;
export const makeAddonRowKey = () => `addon-row-${++_addonKeyCounter}-${Date.now()}`;

/**
 * Shared grid template for the header and each data row, so the columns stay
 * vertically aligned. Columns:
 * Add-on (flex) | Service Date | Pax | Unit Amount | Qty | Line Amount | action.
 */
/*
 * Sized to FIT the booking modal's left column (~718px). The previous
 * 7-column set had a 794px floor — 200px name + 546px of fixed tracks + 48px of
 * gaps — so Line Amount and the remove button were clipped off the right edge
 * once Service Date and Pax were added. Any future column must be checked
 * against this budget, not just appended.
 */
const GRID_TEMPLATE = 'minmax(140px, 2fr) 118px 54px 88px 54px 96px 28px';

const AddonsSection: React.FC<AddonsSectionProps> = ({
  propertyId,
  rows,
  onChange,
  expanded,
  onToggle,
  readOnly,
  checkInDate,
  checkOutDate,
}) => {
  // Cache of every addon option we've shown the user this session, keyed by id.
  // Populated by `loadAddonOptions` and used by the SyutSelect onChange to capture
  // a label snapshot (the SyutSelect's onChange only returns the value, not the
  // option meta — so we look it up here).
  const labelCacheRef = useRef<Record<string, string>>({});

  /**
   * Service type per add-on id, harvested from the same lookup that feeds the
   * dropdown. Pax is only meaningful for MEAL items — a conference hall has no
   * cover count — so the column is enabled from this rather than asking the
   * operator to know which items the kitchen cares about.
   */
  const [serviceTypeById, setServiceTypeById] = useState<Record<string, string>>({});

  // Async loader scoped to the booking's property — so the operator only sees
  // add-ons belonging to the property they're booking against.
  const loadAddonOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      const res = await addonService.getAll({
        searchTerm: inputValue || undefined,
        propertyId: propertyId || undefined,
        pageSize: 50,
        includeInactive: false,
      });
      const opts = (res.data || []).map((a) => ({
        value: a.id,
        label: `${a.addonCode} - ${a.addonName}`,
      }));
      setServiceTypeById((prev: Record<string, string>) => {
        const next = { ...prev };
        (res.data || []).forEach(a => { next[a.id] = a.serviceType || 'SERVICE'; });
        return next;
      });
      // Cache for the onChange handler.
      opts.forEach((o) => {
        labelCacheRef.current[o.value] = o.label;
      });
      return opts;
    },
    [propertyId],
  );

  const total = useMemo(
    () =>
      rows.reduce(
        (sum, r) => sum + Math.max(0, Number(r.unitAmount) || 0) * Math.max(1, Number(r.quantity) || 1),
        0,
      ),
    [rows],
  );

  const handleAddRow = useCallback(() => {
    onChange([
      ...rows,
      {
        _rowKey: makeAddonRowKey(), addonId: '', unitAmount: 0, quantity: 1, remarks: '',
        // Defaulted, not inferred: an implicit day-offset rule would be wrong for
        // late arrivals, and the operator can always move it.
        serviceDate: checkInDate || undefined,
      },
    ]);
  }, [rows, onChange, checkInDate]);

  const handleRemoveRow = useCallback(
    (rowKey: string) => {
      onChange(rows.filter((r) => r._rowKey !== rowKey));
    },
    [rows, onChange],
  );

  const updateRow = useCallback(
    (rowKey: string, patch: Partial<AddonRow>) => {
      onChange(rows.map((r) => (r._rowKey === rowKey ? { ...r, ...patch } : r)));
    },
    [rows, onChange],
  );

  return (
    <div className="booking-section" style={{ marginBottom: '0.75rem' }}>
      {/* Section header */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.625rem 0.75rem',
          borderRadius: '0.375rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <IconSystem.tag size={16} style={{ opacity: 0.85 }} />
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Add-ons
        </span>
        <span
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-muted)',
            marginLeft: '0.25rem',
          }}
        >
          ({rows.length} item{rows.length === 1 ? '' : 's'})
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          Total: {formatCurrency(total)}
        </span>
        <IconSystem.chevronDown
          size={16}
          style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
            opacity: 0.6,
          }}
        />
      </div>

      {/* Content */}
      {expanded && (
        <div
          style={{
            padding: '0.75rem',
            border: '1px solid var(--border-color)',
            borderTop: 'none',
            borderRadius: '0 0 0.375rem 0.375rem',
            background: 'var(--bg-primary)',
          }}
        >
          <div
            style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}
          >
            Add-ons are charged in addition to the room amount. Booking discounts do not apply to add-ons.
          </div>

          {rows.length === 0 && (
            <div
              style={{
                padding: '1rem',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: '0.8rem',
                border: '1px dashed var(--border-color)',
                borderRadius: '0.375rem',
              }}
            >
              No add-ons. Click "+ Add-on" below to add spa services, flowers, airport pickup, etc.
            </div>
          )}

          {/* Table-style layout: one header row at the top, label-less body rows
              below. The grid template is shared between header and body so the
              columns stay aligned. */}
          {rows.length > 0 && (
            /* Fallback for viewports narrower than the budget above: scroll the
               grid inside its own container rather than letting it escape the
               section, which is what clipped the last column before. */
            <div style={{ overflowX: 'auto' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: GRID_TEMPLATE,
                  gap: '0.5rem',
                  padding: '0.375rem 0.5rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0.375rem',
                  marginBottom: '0.375rem',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em',
                  color: 'var(--text-secondary)',
                }}
              >
                <span>Add-on</span>
                <span>Service Date</span>
                <span style={{ textAlign: 'center' }}>Pax</span>
                <span style={{ textAlign: 'right' }}>Unit Amount</span>
                <span style={{ textAlign: 'center' }}>Qty</span>
                <span style={{ textAlign: 'right' }}>Line Amount</span>
                <span aria-hidden />
              </div>

              {rows.map((row) => {
                const seed: SelectOption | undefined =
                  row.addonId && row.addonLabel
                    ? { value: row.addonId, label: row.addonLabel }
                    : undefined;
                const lineAmount =
                  Math.max(0, Number(row.unitAmount) || 0) * Math.max(1, Number(row.quantity) || 1);
                const isMealRow = (serviceTypeById[row.addonId] || '') === 'MEAL';

                return (
                  <div
                    key={row._rowKey}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: GRID_TEMPLATE,
                      gap: '0.5rem',
                      alignItems: 'center',
                      marginBottom: '0.375rem',
                    }}
                  >
                    <SyutSelect
                      isAsync
                      loadOptions={loadAddonOptions}
                      defaultOptions
                      cacheOptions={false}
                      seedOptions={seed ? [seed] : undefined}
                      value={row.addonId}
                      onChange={(val) => {
                        const newId = (val as string) || '';
                        // Pull the label snapshot from the loadOptions-populated cache
                        // (or fall back to the existing seed label if user re-selected).
                        const label = labelCacheRef.current[newId] || row.addonLabel;
                        updateRow(row._rowKey, { addonId: newId, addonLabel: label });
                      }}
                      placeholder="Select add-on..."
                      isClearable={false}
                      isDisabled={readOnly}
                      size="sm"
                      // The rows live inside an overflow-x:auto wrapper (the
                      // clipping fix for the grid itself). overflow-x:auto
                      // forces overflow-y:auto too, so an inline dropdown menu
                      // opens clipped inside the scroll box — portal it out.
                      menuPortalTarget={document.body}
                    />

                    <input
                      type="date"
                      className="form-input"
                      value={row.serviceDate ? row.serviceDate.slice(0, 10) : ''}
                      min={checkInDate || undefined}
                      max={checkOutDate || undefined}
                      onChange={(e) =>
                        updateRow(row._rowKey, { serviceDate: e.target.value || undefined })
                      }
                      readOnly={readOnly}
                      style={{
                        fontSize: '0.8125rem',
                        padding: '0.375rem 0.5rem',
                        background: readOnly ? 'var(--bg-secondary)' : undefined,
                      }}
                    />

                    {/* Pax drives the kitchen's covers forecast, so it is offered
                        only for MEAL items — filling it for a conference hall
                        would put phantom covers on the food plan. */}
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="form-input"
                      value={row.pax ?? ''}
                      placeholder={isMealRow ? '0' : '—'}
                      title={isMealRow ? 'Covers the kitchen must prepare' : 'Pax applies to meal items only'}
                      onChange={(e) =>
                        updateRow(row._rowKey, {
                          pax: e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value, 10) || 0),
                        })
                      }
                      readOnly={readOnly || !isMealRow}
                      disabled={!isMealRow}
                      style={{
                        fontSize: '0.8125rem',
                        padding: '0.375rem 0.5rem',
                        textAlign: 'center',
                        background: readOnly || !isMealRow ? 'var(--bg-secondary)' : undefined,
                      }}
                    />

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-input"
                      value={row.unitAmount}
                      onChange={(e) =>
                        updateRow(row._rowKey, { unitAmount: parseFloat(e.target.value) || 0 })
                      }
                      readOnly={readOnly}
                      style={{
                        fontSize: '0.8125rem',
                        padding: '0.375rem 0.5rem',
                        textAlign: 'right',
                        background: readOnly ? 'var(--bg-secondary)' : undefined,
                      }}
                    />

                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="form-input"
                      value={row.quantity}
                      onChange={(e) =>
                        updateRow(row._rowKey, {
                          quantity: Math.max(1, parseInt(e.target.value, 10) || 1),
                        })
                      }
                      readOnly={readOnly}
                      style={{
                        fontSize: '0.8125rem',
                        padding: '0.375rem 0.5rem',
                        textAlign: 'center',
                        background: readOnly ? 'var(--bg-secondary)' : undefined,
                      }}
                    />

                    {/* Computed line amount — right-aligned numeric display styled
                        to mirror a read-only form input so it visually belongs to
                        the same row as the Unit Amount and Qty inputs (matching
                        border, radius, background, padding). */}
                    <div
                      style={{
                        fontSize: '0.8125rem',
                        padding: '0.375rem 0.5rem',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        border: '1px solid var(--form-border-color)',
                        borderRadius: '0.375rem',
                        background: 'var(--bg-secondary)',
                      }}
                    >
                      {formatCurrency(lineAmount)}
                    </div>

                    {!readOnly ? (
                      <button
                        onClick={() => handleRemoveRow(row._rowKey)}
                        title="Remove add-on"
                        style={{
                          width: '32px',
                          height: '32px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-primary)',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#dc2626',
                        }}
                      >
                        <IconSystem.trash size={14} />
                      </button>
                    ) : (
                      <span aria-hidden />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!readOnly && (
            <button
              onClick={handleAddRow}
              style={{
                marginTop: '0.5rem',
                padding: '0.4rem 0.75rem',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#2563eb',
                background: 'transparent',
                border: '1px dashed #2563eb',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
              }}
            >
              <IconSystem.add size={14} />
              Add-on
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AddonsSection;
